export { route };
export { loadPageRoutes } from './loadPageRoutes';
// Ensure we don't bloat runtime of Server Routing
import { assertClientRouting } from '../../utils/assertRoutingType';
import { isBrowser } from '../../utils/isBrowser';
if (isBrowser()) {
    assertClientRouting();
}
import { assert, assertUsage, hasProp, isPlainObject, objectAssign } from './utils';
import { addComputedUrlProps } from '../addComputedUrlProps';
import { resolvePrecendence } from './resolvePrecedence';
import { resolveRouteString } from './resolveRouteString';
import { resolveRouteFunction } from './resolveRouteFunction';
import { callOnBeforeRouteHook } from './callOnBeforeRouteHook';
import { loadPageRoutes } from './loadPageRoutes';
import { debug } from './debug';
async function route(pageContext) {
    addComputedUrlProps(pageContext);
    const { pageRoutes, onBeforeRouteHook } = await loadPageRoutes(pageContext._pageFilesAll, pageContext._pageConfigs, pageContext._pageConfigGlobal, pageContext._allPageIds);
    debug('Pages routes:', pageRoutes);
    const pageContextAddendum = {};
    if (onBeforeRouteHook) {
        const pageContextAddendumHook = await callOnBeforeRouteHook(onBeforeRouteHook, pageContext);
        if (pageContextAddendumHook) {
            objectAssign(pageContextAddendum, pageContextAddendumHook);
            if (hasProp(pageContextAddendum, '_pageId', 'string') || hasProp(pageContextAddendum, '_pageId', 'null')) {
                // We bypass `vite-plugin-ssr`'s routing
                if (!hasProp(pageContextAddendum, 'routeParams')) {
                    objectAssign(pageContextAddendum, { routeParams: {} });
                }
                else {
                    assert(hasProp(pageContextAddendum, 'routeParams', 'object'));
                }
                objectAssign(pageContextAddendum, {
                    _routingProvidedByOnBeforeRouteHook: true,
                    _routeMatches: 'CUSTOM_ROUTE'
                });
                return { pageContextAddendum };
            }
            // We already assign so that `pageContext.urlOriginal === pageContextAddendum.urlOriginal`; enabling the `onBeforeRoute()` hook to mutate `pageContext.urlOriginal` before routing.
            objectAssign(pageContext, pageContextAddendum);
        }
    }
    objectAssign(pageContextAddendum, {
        _routingProvidedByOnBeforeRouteHook: false
    });
    // `vite-plugin-ssr`'s routing
    const allPageIds = pageContext._allPageIds;
    assert(allPageIds.length >= 0);
    assertUsage(pageContext._pageFilesAll.length > 0 || pageContext._pageConfigs.length > 0, 'No *.page.js file found. You must create at least one *.page.js file.');
    assertUsage(allPageIds.length > 0, "You must create at least one *.page.js file that isn't _default.page.*");
    const { urlPathname } = pageContext;
    assert(urlPathname.startsWith('/'));
    const routeMatches = [];
    await Promise.all(pageRoutes.map(async (pageRoute) => {
        const { pageId, routeType } = pageRoute;
        // Filesytem Routing
        if (pageRoute.routeType === 'FILESYSTEM') {
            const { routeString } = pageRoute;
            const match = resolveRouteString(routeString, urlPathname);
            if (match) {
                const { routeParams } = match;
                routeMatches.push({ pageId, routeParams, routeString, routeType });
            }
            return;
        }
        // Route String defined in `.page.route.js`
        if (pageRoute.routeType === 'STRING') {
            const { routeString } = pageRoute;
            const match = resolveRouteString(routeString, urlPathname);
            if (match) {
                const { routeParams } = match;
                assert(routeType === 'STRING');
                routeMatches.push({
                    pageId,
                    routeString,
                    routeParams,
                    routeType
                });
            }
            return;
        }
        // Route Function defined in `.page.route.js`
        if (pageRoute.routeType === 'FUNCTION') {
            const { routeFunction, allowAsync, routeDefinedAt } = pageRoute;
            const match = await resolveRouteFunction(routeFunction, allowAsync, pageContext, routeDefinedAt);
            if (match) {
                const { routeParams, precedence } = match;
                routeMatches.push({ pageId, precedence, routeParams, routeType });
            }
            return;
        }
        assert(false);
    }));
    resolvePrecendence(routeMatches);
    const winner = routeMatches[0];
    debug(`Route matches for URL \`${urlPathname}\` (in precedence order):`, routeMatches);
    objectAssign(pageContextAddendum, { _routeMatches: routeMatches });
    if (!winner) {
        objectAssign(pageContextAddendum, {
            _pageId: null,
            routeParams: {}
        });
        return { pageContextAddendum };
    }
    {
        const { routeParams } = winner;
        assert(isPlainObject(routeParams));
        objectAssign(pageContextAddendum, {
            _pageId: winner.pageId,
            routeParams: winner.routeParams
        });
    }
    return { pageContextAddendum };
}
